/** Central command registry for the image editor (Tier 3 Group C
 *  Phase 1). Every menu item, toolbar button, and keyboard shortcut
 *  in the editor dispatches through here. Three downstream features
 *  consume the registry directly:
 *
 *    1. The Cmd/Ctrl+K Command Palette (this PR — list + fuzzy
 *       search + dispatch).
 *    2. The Keyboard Shortcut Customization settings panel (next PR
 *       — remap each command's `shortcuts[]`).
 *    3. The Action Recorder (later — captures the sequence of
 *       command ids the user runs and replays it).
 *
 *  Keep this module dependency-free (no React, no Fabric). The
 *  registry is a plain class so each editor instance can own its own
 *  (tests rely on isolation) — the React provider wraps an instance
 *  and exposes it via context. */

export type CommandCategory =
  | 'file'
  | 'edit'
  | 'select'
  | 'image'
  | 'layer'
  | 'view'
  | 'tools'
  | 'transform'
  | 'filter'
  | 'help';

/** A single key combo that triggers a command.
 *
 *  `mod` is the canonical platform modifier — Cmd on macOS, Ctrl on
 *  Windows/Linux. Use `mod` for cross-platform shortcuts (the common
 *  case: Cmd+S / Ctrl+S = Save). Use the explicit `ctrl` flag only
 *  when you want literal Ctrl on every platform (rare; mostly for
 *  shortcuts that conflict with macOS system bindings).
 *
 *  `code` is the KeyboardEvent.code (physical key, layout-independent).
 *  When set it takes precedence over `key` — needed for shortcuts like
 *  Ctrl+= and Ctrl+- that report different `key` values on AZERTY /
 *  QWERTZ / Dvorak. Most shortcuts only need `key`. */
export interface CommandShortcut {
  /** KeyboardEvent.key value (case-insensitive for letters). */
  key: string;
  /** KeyboardEvent.code value (physical key, layout-independent). */
  code?: string;
  /** Cmd on macOS, Ctrl on Windows/Linux. The common case. */
  mod?: boolean;
  /** Literal Ctrl key — independent of platform. */
  ctrl?: boolean;
  shift?: boolean;
  alt?: boolean;
}

/** Runtime context the registry passes to `isAvailable` predicates
 *  and the `run` handler. Everything a command might branch on lives
 *  here — keeps command specs declarative + pure-ish.
 *
 *  When you add a new field, every consumer building a context object
 *  must opt in. The default context used by the registry is intended
 *  for tests / dispatch-from-palette callsites that don't depend on
 *  editor state; the EditorPage provider populates real values. */
export interface CommandContext {
  hasActiveLayer: boolean;
  hasSelection: boolean;
  activeTool: string | null;
  transformActive: boolean;
  pathEditActive: boolean;
  /** True while a contenteditable / TextEditMode is consuming
   *  keystrokes. The registry's keyboard dispatcher uses this to skip
   *  letter-key tool shortcuts while typing (otherwise typing 'v' in
   *  a text layer would switch to the Move tool). */
  isEditingText: boolean;
  /** True while the Command Palette modal is open. The dispatcher
   *  uses this to suppress ALL other shortcuts so palette-internal
   *  keys (arrows, Enter, Esc) don't leak through and trigger an
   *  unrelated editor command. */
  paletteOpen: boolean;
}

export interface CommandSpec {
  /** Stable id, dotted-namespace form (`edit.undo`, `file.save`).
   *  Used as the storage key for keyboard customization and the
   *  recording format for actions — never rename without a migration
   *  story. */
  id: string;
  /** Display label for menus / palette. */
  label: string;
  category: CommandCategory;
  /** One-line description shown in the palette's secondary row. */
  description?: string;
  /** Default key bindings. User customization overrides via a
   *  separate per-user-pref layer (future); the registry stores only
   *  defaults. Zero, one, or multiple combos may map to the same
   *  command (e.g. Redo = Ctrl+Y OR Ctrl+Shift+Z). */
  shortcuts?: readonly CommandShortcut[];
  /** Gates dispatch + visibility in the palette. When false the
   *  command is shown disabled (not hidden) and keyboard dispatch is
   *  a no-op. Omit for always-available commands. */
  isAvailable?: (ctx: CommandContext) => boolean;
  /** Hide from the palette list (still dispatchable via keyboard +
   *  programmatic dispatch). For shortcuts that don't make sense as
   *  discoverable commands — e.g. Escape-cancels-transform. */
  hidden?: boolean;
  /** The actual work. Async tolerated so commands like Save can
   *  await their export pipeline. The registry's dispatch awaits
   *  the result so the Action Recorder can step through serially. */
  run: (ctx: CommandContext) => void | Promise<void>;
}

/** Default context for callsites that don't have real editor state
 *  (palette test mounts, programmatic dispatch tests). Every field
 *  is set to a "nothing special" value — `isAvailable` predicates
 *  written against this context produce conservative results. */
export const DEFAULT_COMMAND_CONTEXT: CommandContext = {
  hasActiveLayer: false,
  hasSelection: false,
  activeTool: null,
  transformActive: false,
  pathEditActive: false,
  isEditingText: false,
  paletteOpen: false,
};

/** Detects macOS once. Pure helper so the registry has no React or
 *  browser-globals dependency at module-eval time — callers pass the
 *  result in. Defaults to navigator.platform sniffing on the
 *  browser; tests and SSR can override. */
export function isMacPlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  // navigator.platform is deprecated but still the most reliable
  // signal in practice; userAgentData.platform is gated behind
  // permissions on Firefox.
  return /Mac|iPhone|iPad|iPod/.test(navigator.platform);
}

/** Format a shortcut for display in menus / tooltips / the palette.
 *  Mirrors platform conventions: ⌘⇧K on macOS, Ctrl+Shift+K
 *  elsewhere. */
export function formatShortcut(s: CommandShortcut, isMac: boolean): string {
  const parts: string[] = [];
  if (isMac) {
    if (s.ctrl) parts.push('⌃');
    if (s.alt) parts.push('⌥');
    if (s.shift) parts.push('⇧');
    if (s.mod) parts.push('⌘');
    parts.push(formatKey(s.key));
    return parts.join('');
  }
  if (s.mod || s.ctrl) parts.push('Ctrl');
  if (s.alt) parts.push('Alt');
  if (s.shift) parts.push('Shift');
  parts.push(formatKey(s.key));
  return parts.join('+');
}

function formatKey(key: string): string {
  // Special-key dispatch BEFORE the length-1 path, because ' ' (the
  // space character) is length-1 and would otherwise format as a
  // literal blank.
  switch (key) {
    case 'ArrowUp':
      return '↑';
    case 'ArrowDown':
      return '↓';
    case 'ArrowLeft':
      return '←';
    case 'ArrowRight':
      return '→';
    case 'Escape':
      return 'Esc';
    case ' ':
      return 'Space';
    default:
      if (key.length === 1) return key.toUpperCase();
      return key;
  }
}

/** True when the keyboard event matches the shortcut spec.
 *
 *  Letter keys match case-insensitively (a vs A is determined by the
 *  Shift flag, not the key character — pressing Shift+a reports
 *  e.key === 'A', and the spec should say shift: true regardless of
 *  its key being 'a' or 'A'). */
export function shortcutMatches(s: CommandShortcut, e: KeyboardEvent, isMac: boolean): boolean {
  // Key / code match. `code` takes precedence when provided.
  if (s.code) {
    if (e.code !== s.code) return false;
  } else {
    if (s.key.length === 1 && e.key.length === 1) {
      if (s.key.toLowerCase() !== e.key.toLowerCase()) return false;
    } else if (s.key !== e.key) {
      return false;
    }
  }
  // Modifiers — `mod` = Cmd on Mac, Ctrl elsewhere. `ctrl` is
  // always literal Ctrl. The spec must NOT set both; we treat them
  // as a logical OR for the actual key check.
  const wantMod = !!s.mod;
  const wantCtrl = !!s.ctrl;
  const gotMod = isMac ? e.metaKey : e.ctrlKey;
  // Literal Ctrl is satisfied by the Ctrl key on every platform —
  // even on Mac, where it is also the "right-click" modifier.
  const gotCtrl = e.ctrlKey;
  if (wantMod && !gotMod) return false;
  if (wantCtrl && !gotCtrl) return false;
  // Reject extraneous modifiers — Cmd+S must NOT fire when the user
  // presses Cmd+Shift+S, because that's a different shortcut.
  if (!wantMod && !wantCtrl) {
    // On Mac, plain shortcuts (bare letters) must not have Cmd
    // pressed; on Win/Linux, must not have Ctrl pressed.
    if (gotMod) return false;
    // ALSO reject literal Ctrl on Mac so plain shortcuts don't fire
    // when the user is pressing Ctrl for something else.
    if (isMac && gotCtrl) return false;
    if (!isMac && e.ctrlKey) return false;
  }
  if (!!s.shift !== e.shiftKey) return false;
  if (!!s.alt !== e.altKey) return false;
  return true;
}

/** Structural equality for shortcut specs. Used by the Settings →
 *  Keyboard dialog's conflict detector — two specs that match the
 *  same physical key combo are considered "the same shortcut" even
 *  if one was typed lowercase and the other upper.
 *
 *  `code` policy: when BOTH sides specify a code, compare codes
 *  (layout-independent match). When only one side has code, fall
 *  back to `key` comparison — the common case is a built-in default
 *  spec like `{key:'s', mod:true}` colliding with a user-recorded
 *  override `{key:'s', code:'KeyS', mod:true}`. Pressing Cmd+S
 *  would fire both; treat them as the same logical combo. */
export function shortcutsEqual(a: CommandShortcut, b: CommandShortcut): boolean {
  const aKey = a.key.length === 1 ? a.key.toLowerCase() : a.key;
  const bKey = b.key.length === 1 ? b.key.toLowerCase() : b.key;
  const keyOrCodeMatch = a.code && b.code ? a.code === b.code : aKey === bKey;
  return (
    keyOrCodeMatch &&
    !!a.mod === !!b.mod &&
    !!a.ctrl === !!b.ctrl &&
    !!a.shift === !!b.shift &&
    !!a.alt === !!b.alt
  );
}

/** Fuzzy-search score for a candidate label against a query.
 *  Returns -1 when there is no match; otherwise a non-negative score
 *  (higher = better). Case-insensitive throughout. */
export function fuzzyScore(label: string, query: string): number {
  if (query.length === 0) return 0;
  const l = label.toLowerCase();
  const q = query.toLowerCase();
  // Exact substring beats everything; word-boundary substring beats
  // mid-word; subsequence is the floor.
  const idx = l.indexOf(q);
  if (idx === 0) return 1000 - l.length; // shorter labels rank higher
  if (idx > 0) {
    // Word boundary: char before is a non-letter (space, dash, ...).
    const prev = l[idx - 1];
    if (prev && /[^a-z0-9]/.test(prev)) return 800 - l.length;
    return 600 - l.length;
  }
  // Subsequence: every char of q appears in l in order. Tighter
  // matches (fewer gaps) score higher.
  let li = 0;
  let lastMatch = -1;
  let gapPenalty = 0;
  for (let qi = 0; qi < q.length; qi++) {
    const c = q[qi];
    let found = -1;
    while (li < l.length) {
      if (l[li] === c) {
        found = li;
        li++;
        break;
      }
      li++;
    }
    if (found < 0) return -1;
    if (lastMatch >= 0) gapPenalty += found - lastMatch - 1;
    lastMatch = found;
  }
  // Cap subsequence score below word-boundary matches.
  return Math.max(0, 400 - gapPenalty * 2 - l.length);
}

export interface CommandSearchResult {
  command: CommandSpec;
  score: number;
}

/** Plain class so each editor instance owns its own registry — the
 *  React provider creates one in a `useRef` and exposes it. Tests
 *  can `new CommandRegistry()` to get a fresh isolated instance. */
export class CommandRegistry {
  private commands = new Map<string, CommandSpec>();
  /** User-set shortcut overrides. Key = command id; value = the
   *  effective shortcut list that supersedes the spec's `shortcuts`
   *  field. Missing key = use spec default. Empty array = explicitly
   *  unbind the command from any keyboard combo (palette / toolbar
   *  still dispatch it). */
  private overrides = new Map<string, readonly CommandShortcut[]>();
  /** Bumped on every mutation (register, unregister, override
   *  change). Subscribers (typically React components via the
   *  useRegistrySnapshot hook) re-render when this changes — that's
   *  what makes "Crop (C)" tooltips reactively update to "Crop (X)"
   *  the instant the user remaps the binding in Settings.
   *
   *  Phase 4 (Action Recorder) consideration: every command dispatch
   *  will likely also bump version when the recorder is on. With ~44
   *  commands × M subscribers × 200-step recorded playbacks, the
   *  global broadcast can thrash. Mitigation when that lands: split
   *  into per-command-id channels so a component watching
   *  `getEffectiveShortcuts('tool.crop')` only re-renders on
   *  crop-specific mutations. Out of scope here; the global pattern
   *  is fine for Phase 2's ~120 subscribers. */
  private version = 0;
  private listeners = new Set<() => void>();

  /** Subscribe to mutation events. Returns an unsubscribe function.
   *  Wired into React via the `useRegistrySnapshot` hook (which
   *  uses `useSyncExternalStore` for tearing-free reads). */
  subscribe(listener: () => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /** Monotonic version stamp. `useSyncExternalStore`'s snapshot
   *  function reads this; React diffs the returned value to decide
   *  whether to re-render. */
  getVersion(): number {
    return this.version;
  }

  /** Notify every subscribed listener that a mutation has occurred.
   *  Synchronous + atomic: every listener runs before the calling
   *  mutation method returns, so no listener can observe a partial
   *  state. The version bump precedes listener calls so
   *  `useSyncExternalStore`'s snapshot comparison sees the new value. */
  private notify(): void {
    this.version++;
    for (const l of this.listeners) l();
  }

  /** Register a command. Returns an unregister function — the typical
   *  pattern is calling this from a React effect and returning the
   *  unregisterer as the cleanup. */
  register(spec: CommandSpec): () => void {
    if (this.commands.has(spec.id)) {
      console.warn(`[command-registry] overwriting existing command "${spec.id}"`);
    }
    this.commands.set(spec.id, spec);
    this.notify();
    return () => {
      // Only delete when the spec stored is the one we registered —
      // protects against effect-cleanup-after-rerender races where a
      // later registration has already replaced the slot.
      if (this.commands.get(spec.id) === spec) {
        this.commands.delete(spec.id);
        this.notify();
      }
    };
  }

  unregister(id: string): void {
    if (this.commands.delete(id)) this.notify();
  }

  get(id: string): CommandSpec | undefined {
    return this.commands.get(id);
  }

  /** All commands in insertion order. Hidden commands included —
   *  filter in the palette layer. */
  list(): CommandSpec[] {
    return Array.from(this.commands.values());
  }

  /** Replace the shortcut list for a command at runtime — the
   *  mechanism behind the Keyboard Shortcut Customization settings
   *  panel.
   *
   *    - `setShortcutOverrides(id, [...])` → custom bindings
   *    - `setShortcutOverrides(id, [])` → explicit unbind (palette /
   *      toolbar still dispatch it, but no keyboard combo will)
   *    - `setShortcutOverrides(id, null)` → revert to the spec's
   *      declared `shortcuts`
   *
   *  No-ops silently for unknown ids — protects callers that load
   *  a stale persisted override map after a command has been
   *  renamed or removed. */
  setShortcutOverrides(id: string, shortcuts: readonly CommandShortcut[] | null): void {
    if (!this.commands.has(id)) return;
    if (shortcuts === null) {
      this.overrides.delete(id);
    } else {
      this.overrides.set(id, shortcuts);
    }
    this.notify();
  }

  /** The shortcut list `matchShortcut` will use for this command —
   *  override when set, spec default otherwise. Always defined
   *  (returns `[]` for an unknown id or a spec without shortcuts).
   *  Exposed so settings UIs can render the "current" state and
   *  decide whether to show a Reset affordance. */
  getEffectiveShortcuts(id: string): readonly CommandShortcut[] {
    const override = this.overrides.get(id);
    if (override !== undefined) return override;
    return this.commands.get(id)?.shortcuts ?? [];
  }

  /** Bulk apply — the load-from-storage path. Replaces the entire
   *  override map; ids missing from `map` revert to spec defaults.
   *  Unknown ids are silently skipped (see setShortcutOverrides). */
  setAllShortcutOverrides(map: Record<string, readonly CommandShortcut[]>): void {
    this.overrides.clear();
    for (const [id, shortcuts] of Object.entries(map)) {
      if (this.commands.has(id)) this.overrides.set(id, shortcuts);
    }
    this.notify();
  }

  /** Drop every override; every command reverts to its spec
   *  default. Powers the Settings panel's "Reset All" button. */
  clearShortcutOverrides(): void {
    if (this.overrides.size === 0) return;
    this.overrides.clear();
    this.notify();
  }

  /** Snapshot of the current override map. Used by the Settings
   *  panel's Export JSON affordance + the auto-persist flow. */
  getAllShortcutOverrides(): Record<string, readonly CommandShortcut[]> {
    const out: Record<string, readonly CommandShortcut[]> = {};
    for (const [id, shortcuts] of this.overrides) out[id] = shortcuts;
    return out;
  }

  /** First command whose shortcut matches the event AND whose
   *  availability gate passes. Insertion order acts as priority —
   *  if two commands share a shortcut (the canonical case is the
   *  modal-mode Enter / Escape commands that each guard on a
   *  different context flag), the earlier-registered one wins. A
   *  shortcut match with a failing `isAvailable` falls through to
   *  the next candidate rather than blocking the search — this is
   *  what lets Enter map to crop / transform / pen / path-edit by
   *  context instead of by a single hardcoded handler.
   *
   *  Shortcut lookup goes through `getEffectiveShortcuts`, so user
   *  overrides (Keyboard Shortcut Customization) supersede the
   *  declared defaults without changing the spec.
   *
   *  Caller is responsible for `preventDefault` — registry never
   *  touches the event. */
  matchShortcut(e: KeyboardEvent, ctx: CommandContext, isMac: boolean): CommandSpec | null {
    for (const cmd of this.commands.values()) {
      const shortcuts = this.getEffectiveShortcuts(cmd.id);
      if (shortcuts.length === 0) continue;
      for (const s of shortcuts) {
        if (!shortcutMatches(s, e, isMac)) continue;
        if (cmd.isAvailable && !cmd.isAvailable(ctx)) {
          // Same-shortcut, different-context — keep searching.
          break;
        }
        return cmd;
      }
    }
    return null;
  }

  /** Search (palette). Empty query returns every visible command in
   *  insertion order with score 0; non-empty query returns scored
   *  matches sorted high-to-low. Hidden commands are excluded. */
  search(query: string): CommandSearchResult[] {
    const visible = this.list().filter((c) => !c.hidden);
    if (query.trim().length === 0) {
      return visible.map((command) => ({ command, score: 0 }));
    }
    const results: CommandSearchResult[] = [];
    for (const command of visible) {
      const score = fuzzyScore(command.label, query);
      if (score >= 0) results.push({ command, score });
    }
    results.sort((a, b) => b.score - a.score || a.command.label.localeCompare(b.command.label));
    return results;
  }

  /** Run a command by id. No-ops silently when the id is unknown or
   *  the command's availability gate is false. Returns the run()
   *  promise so callers can await side-effects when needed. */
  async dispatch(id: string, ctx: CommandContext): Promise<void> {
    const cmd = this.commands.get(id);
    if (!cmd) return;
    if (cmd.isAvailable && !cmd.isAvailable(ctx)) return;
    await cmd.run(ctx);
  }

  /** Find the command (if any) that currently holds the given
   *  shortcut combo, IGNORING `excludeId` — pass the command being
   *  edited so its own existing bindings don't show as conflicts
   *  with themselves.
   *
   *  Powers the Settings → Keyboard dialog's "Already used by X"
   *  warning. Returns the first conflict (insertion order) — the
   *  same combo can't be assigned to two commands at once, but if
   *  somehow it is (e.g. a malformed import), this surfaces the
   *  earliest holder. */
  findShortcutConflict(shortcut: CommandShortcut, excludeId: string): CommandSpec | null {
    for (const cmd of this.commands.values()) {
      if (cmd.id === excludeId) continue;
      const shortcuts = this.getEffectiveShortcuts(cmd.id);
      for (const s of shortcuts) {
        if (shortcutsEqual(s, shortcut)) return cmd;
      }
    }
    return null;
  }
}
